import { FunnelIcon, XIcon } from '@phosphor-icons/react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import {
  hardCriteria,
  type CriteriaPayload,
  type HardCriterionKey,
} from '@entities/search-session';

import styles from './Criteria.module.css';

interface HardCriteriaBlockProps {
  payload: CriteriaPayload;
  disabled: boolean;
  /** Снятие обязательности марки — одно действие (FE-390). */
  onDropMakeRequirement: () => void;
  dropping: boolean;
  /** Снятие обязательности модели: «только Golf» → «Golf и похожие» (FE-530). */
  onDropModelRequirement: () => void;
  droppingModel: boolean;
}

/**
 * Блок «Обязательные условия» (FE-390, MVP_PRD §10, MATCHING §3.5).
 *
 * Зачем он нужен отдельно от списка критериев. Hard-критерии — это то, что
 * **отсеивает автомобили**, и разбросанные по разделам они читаются как
 * пожелания. Пустая выдача складывается ровно из них, поэтому пользователь
 * обязан видеть весь список сразу и понимать, что каждое условие делает.
 *
 * Снять здесь можно только обязательность марки: остальные условия — это сами
 * значения критериев, и меняются они там, где заданы, или в диалоге. Кнопки
 * «сделать обязательным» в блоке нет и быть не может — жёсткость назначается
 * подтверждением пользователя, а не переключателем (MATCHING §3.5).
 */
export function HardCriteriaBlock({
  payload,
  disabled,
  onDropMakeRequirement,
  dropping,
  onDropModelRequirement,
  droppingModel,
}: HardCriteriaBlockProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const keys: HardCriterionKey[] = hardCriteria(payload);

  if (keys.length === 0) return null;

  return (
    <section className={styles.hard} aria-labelledby={titleId}>
      <h3 id={titleId} className={styles.hardTitle}>
        <FunnelIcon size={16} weight="bold" aria-hidden="true" />
        {t('criteria.hard.title')}
      </h3>
      <p className={styles.hardLead}>{t('criteria.hard.lead')}</p>
      <ul className={styles.hardList}>
        {keys.map((key) => (
          <li key={key} className={styles.hardItem}>
            <span className={styles.hardItemText}>{t(`criteria.hard.item.${key}`)}</span>
            {key === 'makesRequired' ? (
              <button
                type="button"
                className={styles.hardDrop}
                onClick={onDropMakeRequirement}
                disabled={disabled}
              >
                <XIcon size={14} weight="bold" aria-hidden="true" />
                {dropping ? t('criteria.hard.dropping') : t('criteria.hard.dropMakeRequirement')}
              </button>
            ) : null}
            {key === 'modelsRequired' ? (
              <button
                type="button"
                className={styles.hardDrop}
                onClick={onDropModelRequirement}
                disabled={disabled}
              >
                <XIcon size={14} weight="bold" aria-hidden="true" />
                {droppingModel
                  ? t('criteria.hard.dropping')
                  : t('criteria.hard.dropModelRequirement')}
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
